import { useState, useRef, useEffect, useMemo } from "react";
//import { useGetTitle } from "../../../hooks/useGetTitle";
import { useNavigate, useParams } from "react-router-dom";
//import { toast } from "react-toastify";

import {
  TextField,
  Box,
  Button,
  Alert,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import styled from "styled-components";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";

import { useProjectApi } from "../../../hooks/useProjectApi";
import {
  useGetTableData,
  useGetTableDataInQueryString,
  useCreateTableData,
  usePutTableData,
  useDeleteTableData,
} from "../../../components/Table/hooks";

import { standardSituationColumns } from "../../../constants/columns";
import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import Buttons from "../../../../common/Buttons";
import {
  BreadcrumbsTable,
  ConfirmDialog,
  ConfirmDialogSingle,
  SnackbarAlert,
} from "../../../components/common/HelperFunc";
import * as message from "../../../constants/messages";
import { FONT_PROPS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1199px) {
    padding: 12px;
    overflow: auto;
    overflow-x: hidden;
  }
`;

// 커스텀 MenuItem 스타일
const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  fontFamily: `${FONT_PROPS.PRIMARY} !important`,
  lineHeight: `${FONT_PROPS} !important`,
  fontWeight: `${FONT_PROPS.MEDIUM} !important`, // 폰트 굵기 medium
  color: `${FONT_PROPS.DARK} !important`, // 기본 텍스트 검정
  backgroundColor: "#ffffff", // 기본 배경 흰색
  "&:hover": {
    backgroundColor: "lightgray", // 마우스 올릴 때 연한 회색
  },
  "&.Mui-selected": {
    backgroundColor: "white !important", // 선택된 항목도 흰색 유지
    color: "black",
  },
  "&.Mui-selected:hover": {
    backgroundColor: "lightgray !important", // 선택된 상태에서 호버 시 연한 회색 유지
  },
}));

const normalizeDateText = (value) => {
  if (!value) return "";
  return String(value).replace("T", " ").slice(0, 16);
};

const buildSituationRegisterLink = (groupCode, projectCode, row) => {
  const params = new URLSearchParams();
  if (row?.danger_cause_id) params.set("danger_cause_id", row.danger_cause_id);
  if (row?.danger_category) params.set("danger_category", row.danger_category);
  if (row?.danger_cause) params.set("danger_cause", row.danger_cause);
  if (row?.danger_description) params.set("keyword", row.danger_description);
  return `/safe/${groupCode}/${projectCode}/register/cause?${params.toString()}`;
};

const buildWorkerProposalSourceMap = (proposals = []) => {
  const map = new Map();
  const approvedProposals = (proposals || [])
    .filter((proposal) => proposal.status === "approved" && proposal.approved_danger_cause_id)
    .sort((a, b) => {
      const aDate = String(a.reviewed_at || a.created_at || "");
      const bDate = String(b.reviewed_at || b.created_at || "");
      if (aDate !== bDate) return bDate.localeCompare(aDate);
      return Number(b.proposal_id || 0) - Number(a.proposal_id || 0);
    });

  approvedProposals.forEach((proposal) => {
    const dangerCauseId = Number(proposal.approved_danger_cause_id);
    const current = map.get(dangerCauseId);
    if (!current) {
      map.set(dangerCauseId, {
        count: 1,
        latest: proposal,
      });
      return;
    }
    current.count += 1;
  });

  return map;
};

function SourceChip({ row }) {
  const isWorkerProposal = Number(row.worker_proposal_source) === 1;

  return (
    <Chip
      size="small"
      color={isWorkerProposal ? "warning" : "default"}
      variant={isWorkerProposal ? "filled" : "outlined"}
      icon={isWorkerProposal ? <PersonSearchIcon /> : undefined}
      label={isWorkerProposal ? "근로자 제안" : "일반 등록"}
      sx={{
        fontWeight: FONT_PROPS.SEMIBOLD,
        "& .MuiChip-icon": { fontSize: 16 },
      }}
    />
  );
}

function SummaryCard({ icon, label, value, helper, color = "#2563eb", active = false, onClick }) {
  const clickable = typeof onClick === "function";

  return (
    <Paper
      component={clickable ? "button" : "div"}
      type={clickable ? "button" : undefined}
      onClick={onClick}
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: active ? color : "divider",
        borderRadius: 2,
        bgcolor: active ? `${color}0f` : "#fff",
        height: "100%",
        width: "100%",
        textAlign: "left",
        appearance: "none",
        font: "inherit",
        color: "inherit",
        cursor: clickable ? "pointer" : "default",
        transition: "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease, transform 160ms ease",
        boxShadow: active ? `0 0 0 1px ${color}22` : "none",
        "&:hover": clickable
          ? {
              borderColor: color,
              boxShadow: `0 8px 18px ${color}18`,
              transform: "translateY(-1px)",
            }
          : undefined,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            color,
            bgcolor: `${color}18`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: FONT_PROPS.SEMIBOLD }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function SituationOverview({
  summary,
  sourceFilter,
  onSourceFilterChange,
  searchStatus,
}) {
  return (
    <Stack spacing={1.5} sx={{ mb: 1.5 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "#fff",
        }}
      >
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <ManageSearchIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                유해·위험요인 마스터
              </Typography>
            </Stack>
            <Typography color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 840 }}>
              근로자 제안으로 승인된 유해·위험요인을 기존 마스터와 함께 관리합니다. 근로자 제안 출신 항목은 배지와 원문 이력으로 구분됩니다.
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        <SummaryCard
          icon={<FactCheckIcon />}
          label="현재 조회"
          value={`${summary.total}건`}
          helper={`${searchStatus.danger_category || "전체"} / ${searchStatus.danger_cause || "전체"}`}
          active={sourceFilter === "all"}
          onClick={() => onSourceFilterChange("all")}
        />
        <SummaryCard
          icon={<PersonSearchIcon />}
          label="근로자 제안 출신"
          value={`${summary.worker}건`}
          helper="승인된 제안에서 생성"
          color="#f59e0b"
          active={sourceFilter === "worker"}
          onClick={() => onSourceFilterChange("worker")}
        />
        <SummaryCard
          icon={<AssignmentTurnedInIcon />}
          label="일반 등록"
          value={`${summary.manual}건`}
          helper="직접 등록된 마스터"
          color="#16a34a"
          active={sourceFilter === "manual"}
          onClick={() => onSourceFilterChange("manual")}
        />
        <SummaryCard
          icon={<ManageSearchIcon />}
          label="화면 표시"
          value={`${summary.visible}건`}
          helper={sourceFilter === "worker" ? "근로자 제안만" : sourceFilter === "manual" ? "일반 등록만" : "전체 출처"}
          color="#2563eb"
        />
      </Box>

    </Stack>
  );
}

///////////////////////////////
// 위험발생상황 및 결과 컴포넌트
///////////////////////////////
const PARENT_TITLE = "정보관리";
const CHILD_TITLE = "위험발생상황 및 결과";
export function StandardSituation() {
  const { group_code, project_code } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTabletPortrait = useMediaQuery(
    "(orientation: portrait) and (min-width: 900px) and (max-width: 1400px)"
  );
  const isReadOnly = false;

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회에 필요한 조건을 상태로 관리합니다. (위험분류, 위험원인, DB 전체 검색)
  const [searchStatus, setSearchStatus] = useState({});
  // 데이터 조회에 필요한 쿼리스트링을 만듭니다.
  const qeuryString = {
    params: {
      // 쿼리스트링 만들때 params 이름을 사용하는게 규칙입니다.
      danger_category: searchStatus.danger_category,
      danger_cause: searchStatus.danger_cause,
      globalDBFilter: searchStatus.globalDBFilter,
    },
  };
  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getSafeSituation",
    group_code,
    project_code,
    qeuryString
  );
  const getWorkerProposalData = useGetTableData(
    "request",
    "/worker-hazard-proposals",
    group_code,
    project_code
  );
  // 데이터 생성 요청
  const postData = useCreateTableData(
    "request",
    "/postSafeSituation",
    group_code,
    project_code
  );
  // 데이터 수정 요청
  const putData = usePutTableData(
    "request",
    "/putSafeSituation",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteSafeSituation",
    group_code,
    project_code
  );

  // 필터링 목록 조회 요청
  const getFilterListData = useGetTableData(
    "request",
    "/getSafeSituationFilterList",
    group_code,
    project_code
  );
  // 위험분류, 위험원인 카테고리를 default 상태로 데이터를 초기화합니다.
  useEffect(() => {
    // 데이터가 로드되지 않았거나 비어 있는 경우 처리 중단
    if (!getFilterListData.data || getFilterListData.data.length === 0) {
      return;
    }
    //console.log(getFilterListData.data);

    const data = {
      globalDBFilter: "", // 전체 검색 텍스트 초기값 비워두기 (DB 조회용도)
      danger_category: getFilterListData.data[0].danger_category_filter,
      danger_cause:
        getFilterListData.data[0].danger_cause_filters[0].danger_cause,
    };

    setSearchStatus(data);
  }, [getFilterListData.data]);

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: "",
    severity: "", // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = "success") => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: "",
      severity: "",
    });
  };

  // 메시지 박스의 알람 메시지
  const [messageAlarm, setMessageAlarm] = useState({
    isOpen: false,
    title: "",
    message: "",
  });
  const openAlarmMessage = (title = "", message = "") => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: true,
      title: title,
      message: message,
    });
  };
  const closeAlarmMessage = () => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: false,
      title: "",
      message: "",
    });
    //console.log('❌ 취소 눌림');
  };

  // 위험분류 목록
  const dangerCategoryList = Array.from(
    new Set(getFilterListData.data.map((item) => item.danger_category_filter))
  );
  // 위험원인 목록
  const dangerCauseList = getFilterListData.data
    .filter((item) => {
      return item.danger_category_filter === searchStatus.danger_category;
    })
    .flatMap((item) => {
      return item.danger_cause_filters.map((filter) => filter.danger_cause);
    });

  // 위험원인 목록을 위험분류에 따라 가져오는 함수
  const getDangerCauseFilters = (dangerCategory) => {
    const index = getFilterListData.data.findIndex(
      (item) => item.danger_category_filter === dangerCategory
    );
    if (index !== -1) {
      return getFilterListData.data[index].danger_cause_filters;
    } else return null;
  };

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => {
    tableRef.current?.clearSelection();
  };
  const getSelectedRows = () => {
    return tableRef.current?.getSelectedRows();
  };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(""); // 검색 텍스트 상태
  const [globalDBFilter, setGlobalDBFilter] = useState(""); // 데이터베이스 검색 텍스트 상태
  const [showAdminMode, setShowAdminMode] = useState(false); // 관리자 모드 활성화 여부
  const [sourceFilter, setSourceFilter] = useState("all"); // 출처 필터
  const [selectedSituationRow, setSelectedSituationRow] = useState(null);

  const workerProposalSourceMap = useMemo(
    () => buildWorkerProposalSourceMap(getWorkerProposalData.data),
    [getWorkerProposalData.data]
  );

  const situationDataWithSource = useMemo(() => {
    return (getData.data || []).map((row) => {
      const proposalSource = workerProposalSourceMap.get(Number(row.danger_cause_id));
      const latest = proposalSource?.latest;
      const isWorkerSource =
        Number(row.worker_proposal_source) === 1 || Boolean(proposalSource);

      return {
        ...row,
        worker_proposal_source: isWorkerSource ? 1 : 0,
        worker_proposal_count:
          Number(row.worker_proposal_count || 0) || proposalSource?.count || 0,
        latest_worker_proposal_id:
          row.latest_worker_proposal_id || latest?.proposal_id || null,
        latest_worker_proposer_name:
          row.latest_worker_proposer_name || latest?.proposer_name || null,
        latest_worker_proposal_text:
          row.latest_worker_proposal_text || latest?.proposal_text || null,
        latest_worker_location_text:
          row.latest_worker_location_text || latest?.location_text || null,
        latest_worker_machine_text:
          row.latest_worker_machine_text || latest?.machine_text || null,
        latest_worker_reviewed_at:
          row.latest_worker_reviewed_at || latest?.reviewed_at || null,
        __bgColor: isWorkerSource ? "#fffaf0" : row.__bgColor,
      };
    });
  }, [getData.data, workerProposalSourceMap]);

  const filteredSituationData = useMemo(() => {
    if (sourceFilter === "worker") {
      return situationDataWithSource.filter((row) => Number(row.worker_proposal_source) === 1);
    }
    if (sourceFilter === "manual") {
      return situationDataWithSource.filter((row) => Number(row.worker_proposal_source) !== 1);
    }
    return situationDataWithSource;
  }, [situationDataWithSource, sourceFilter]);

  const situationSummary = useMemo(() => {
    const worker = situationDataWithSource.filter(
      (row) => Number(row.worker_proposal_source) === 1
    ).length;
    return {
      total: situationDataWithSource.length,
      worker,
      manual: situationDataWithSource.length - worker,
      visible: filteredSituationData.length,
    };
  }, [filteredSituationData.length, situationDataWithSource]);

  const situationColumns = useMemo(() => {
    const descriptionColumn = standardSituationColumns.find(
      (column) => column.accessorKey === "danger_description"
    );
    const otherColumns = standardSituationColumns.filter(
      (column) => column.accessorKey !== "danger_description"
    );
    const compactOtherColumns = otherColumns.map((column) => {
      if (!isTabletPortrait) return column;
      const sizeMap = {
        danger_category: 110,
        danger_cause: 130,
      };
      return {
        ...column,
        size: sizeMap[column.accessorKey] || column.size,
        muiTableBodyCellProps: {
          ...column.muiTableBodyCellProps,
          sx: {
            ...(column.muiTableBodyCellProps?.sx || {}),
            whiteSpace: "normal",
            wordBreak: "keep-all",
            overflowWrap: "anywhere",
            lineHeight: 1.45,
            py: 0.75,
          },
        },
      };
    });

    return [
      {
        accessorKey: "worker_proposal_source",
        header: "출처",
        size: isTabletPortrait ? 96 : 140,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
        },
        Cell: ({ row }) => (
          <Stack alignItems="center" spacing={0.5}>
            <SourceChip row={row.original} />
            {Number(row.original.worker_proposal_count) > 1 ? (
              <Typography variant="caption" color="text.secondary">
                {row.original.worker_proposal_count}건
              </Typography>
            ) : null}
          </Stack>
        ),
      },
      ...compactOtherColumns,
      {
        ...descriptionColumn,
        size: isTabletPortrait ? 420 : descriptionColumn?.size,
        Cell: ({ row }) => {
          const original = row.original;
          const isWorkerProposal = Number(original.worker_proposal_source) === 1;
          return (
            <Box sx={{ py: 0.75, pr: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: FONT_PROPS.MEDIUM,
                  lineHeight: 1.55,
                  wordBreak: "keep-all",
                  overflowWrap: "anywhere",
                }}
              >
                {original.danger_description}
              </Typography>
              {isWorkerProposal ? (
                <Tooltip title={original.latest_worker_proposal_text || ""} placement="top-start">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    최근 제안 #{original.latest_worker_proposal_id || "-"} ·{" "}
                    {original.latest_worker_proposer_name || "제안자 미확인"} ·{" "}
                    {normalizeDateText(original.latest_worker_reviewed_at) || "검토일 미확인"}
                  </Typography>
                </Tooltip>
              ) : null}
            </Box>
          );
        },
      },
    ];
  }, [isTabletPortrait]);

  const goRegisterCause = () => {
    navigate(`/safe/${group_code}/${project_code}/register/cause`);
  };

  const goSelectedRegisterCause = (row = selectedSituationRow) => {
    if (!row) {
      goRegisterCause();
      return;
    }
    navigate(buildSituationRegisterLink(group_code, project_code, row), {
      state: {
        selectedSituation: {
          danger_cause_id: row.danger_cause_id,
          danger_category: row.danger_category,
          danger_cause: row.danger_cause,
          danger_description: row.danger_description,
          worker_proposal_source: row.worker_proposal_source,
          latest_worker_proposal_id: row.latest_worker_proposal_id,
        },
      },
    });
  };

  const customSituationTableTopToolbar = (table) => (
    <Box
      width="100%"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={2}
      sx={{
        gap: 1.5,
        flexWrap: { xs: "wrap", xl: "nowrap" },
      }}
    >
      {/* 왼쪽 영역 */}
      <Box
        display="flex"
        alignItems="center"
        gap={1.25}
        ml={{ xs: 0, xl: 4 }}
        flexWrap="wrap"
        sx={{
          minWidth: 0,
          flex: "1 1 auto",
          "& .MuiTypography-root": {
            whiteSpace: "nowrap",
          },
        }}
      >
        {/* 위험분류 카테고리 */}
        <FormControl size="small">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Typography
              sx={{
                fontWeight: FONT_PROPS.SEMIBOLD,
                fontSize: 15,
                color: theme.text,
              }}
            >
              위험분류
            </Typography>
            <Select
              value={searchStatus.danger_category || ""}
              displayEmpty
              onChange={(e) => {
                //console.log(e.target.value);
                const dangerCategory = e.target.value;
                const dangerCauseFilter = getDangerCauseFilters(
                  e.target.value
                )?.[0]; // 위험원인 초기화
                setSearchStatus({
                  ...searchStatus,
                  danger_category: dangerCategory,
                  danger_cause: dangerCauseFilter.danger_cause,
                  globalDBFilter: "", // 전체 검색 텍스트 초기화
                });
              }}
              sx={{
                width: "150px",
                fontWeight: FONT_PROPS.SEMIBOLD,
                color: theme.text,
                fontSize: 15,
              }}
            >
              {/* 필터 목록 */}
              {dangerCategoryList.map((category, index) => (
                <MenuItem
                  key={index}
                  value={category}
                  sx={{
                    fontWeight: FONT_PROPS.SEMIBOLD,
                    color: theme.text,
                    fontSize: 15,
                  }}
                >
                  {category}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 위험원인 카테고리 */}
        <FormControl size="small">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Typography
              sx={{
                fontWeight: FONT_PROPS.SEMIBOLD,
                fontSize: 15,
                color: theme.text,
              }}
            >
              위험원인
            </Typography>
            <Select
              value={searchStatus.danger_cause || ""}
              displayEmpty
              onChange={(e) => {
                //console.log(e.target.value);
                const dangerCause = e.target.value;
                setSearchStatus({
                  ...searchStatus,
                  danger_cause: dangerCause,
                  globalDBFilter: "", // 전체 검색 텍스트 초기화
                });
              }}
              sx={{
                width: "200px",
                fontWeight: FONT_PROPS.SEMIBOLD,
                color: theme.text,
                fontSize: 15,
              }}
            >
              {/* 필터 목록 */}
              {dangerCauseList.map((cause, index) => (
                <MenuItem
                  key={index}
                  value={cause}
                  sx={{
                    fontWeight: FONT_PROPS.SEMIBOLD,
                    color: theme.text,
                    fontSize: 15,
                  }}
                >
                  {cause}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 구분선 */}
        <Divider
          orientation="vertical"
          sx={{
            height: 40, // 원하는 높이
            borderRightWidth: 2,
            borderColor: theme.palette.divider,
            marginX: 1, // 좌우 간격
          }}
        />
        {/* 전체 검색 필드 */}
        <FormControl size="small">
          <TextField
            size="small"
            placeholder="위험발생상황 및 결과 전체 검색"
            variant="outlined"
            value={globalDBFilter}
            onChange={(e) => {
              setGlobalDBFilter(e.target.value);
            }}
          />
        </FormControl>
        {/* 전체 검색 버튼 */}
        <Buttons.Custom
          startIcon={<SearchIcon />}
          onClick={() => {
            if (globalDBFilter === "") {
              //console.log('클릭전 필드 입력을 하세요.');
              openAlarmMessage("안내", "전체 검색 전, 검색어를 입력하세요.");
              return;
            }
            setSearchStatus({
              ...searchStatus,
              globalDBFilter: globalDBFilter,
            });
            //console.log("전체 검색 클릭됨");
          }}
          sx={{ padding: "12px 16px" }}
        >
          전체 검색
        </Buttons.Custom>
      </Box>

      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {!isReadOnly && selectedSituationRow ? (
          <Buttons.Custom
            startIcon={<AssignmentTurnedInIcon />}
            onClick={() => goSelectedRegisterCause()}
            sx={{ padding: "12px 16px" }}
          >
            선택 항목 연결
          </Buttons.Custom>
        ) : null}
        {/* 검색창 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>

          <Collapse in={showSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setGlobalFilter("");
                          table.setGlobalFilter("");
                          setShowSearch(false); // 닫기
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Collapse>
        </Box>
        {/* 버튼 */}
        {!isReadOnly ? (
          <>
            <Buttons.Add
              onClick={() => openAddModal()} // 행 생성 다이얼로그 열기
              sx={{ padding: "12px 16px" }}
            />
            {showAdminMode && (
              <Buttons.Delete
                onClick={openDeleteModal} // 행 삭제 로직 실행
                disabled={table.getSelectedRowModel().rows.length === 0}
                sx={{ padding: "12px 16px" }}
              >
                데이터 삭제
              </Buttons.Delete>
            )}
            {/* 관리자 모드 */}
            <FormControl component="fieldset">
              <FormControlLabel
                value="top"
                control={
                  <Switch
                    color="primary"
                    checked={showAdminMode}
                    onChange={(event) => setShowAdminMode(event.target.checked)}
                  />
                }
                label="관리자모드"
                labelPlacement="end" // bottom,end,start,top
                sx={{ marginLeft: "4px" }}
              />
            </FormControl>
          </>
        ) : null}
      </Box>
    </Box>
  );

  // 위험발생상황 및 결과 모달 상태 관리
  const [showAddModal, setShowAddModal] = useState({ isOpen: false });
  const openAddModal = () => setShowAddModal({ isOpen: true });
  const closeAddModal = () => setShowAddModal({ isOpen: false });
  // DB 업데이트 (추가 데이터)
  const updateAddData = async (addData) => {
    try {
      //console.log("새로운 데이터: ", addData);

      const data = {
        danger_category: addData.danger_category, // 위험분류
        danger_cause: addData.danger_cause, // 위험원인
        danger_description: addData.danger_description, // 위험요인 발생상황 및 결과
      };

      // 데이터베이스 업데이트
      const result = await postData.mutateAsync(data);
      if (result.status === 200) {
        openDataAlert(message.addSuccess);
      }

      // 생성 모달 초기화
      closeAddModal();
      // 사본 추가 모달 초기화
      closeCopyModal();
    } catch (error) {
      console.error("데이터 추가 중 오류 발생:", error);
    }
  };

  // 테이블의 행(row) 수정 모달 상태 관리
  const [showEditModal, setEditModal] = useState({
    isOpen: false,
    rowData: null,
  });
  const openEditModal = (rowData) =>
    setEditModal({ isOpen: true, rowData: rowData });
  const closeEditModal = () => setEditModal({ isOpen: false, rowData: null });

  // 테이블의 행(row) 사본 추가 상태 관리
  const [showCopyModal, setShowCopyModal] = useState({
    isOpen: false,
    rowData: null,
  });
  const openCopyModal = (rowData) =>
    setShowCopyModal({ isOpen: true, rowData: rowData });
  const closeCopyModal = () =>
    setShowCopyModal({ isOpen: false, rowData: null });

  // 테이블의 행(row) 수정 액션
  const customEditSituationRowAction = (rowData) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Buttons.Edit
          onClick={(e) => {
            e.stopPropagation(); // 이벤트 전파 중단
            openEditModal(rowData);
          }}
          sx={{ padding: "8px 12px" }}
        />
        <Buttons.Custom
          onClick={(e) => {
            e.stopPropagation(); // 이벤트 전파 중단
            openCopyModal(rowData);
          }}
          startIcon={<ContentCopyIcon />}
          sx={{ padding: "8px 12px" }}
        >
          사본 추가
        </Buttons.Custom>
      </Box>
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (updateData) => {
    try {
      //console.log(updateData);

      const data = {
        danger_cause_id: updateData.danger_cause_id, // 위험원인 ID
        danger_category: updateData.danger_category, // 위험분류
        danger_cause: updateData.danger_cause, // 위험원인
        danger_description: updateData.danger_description, // 위험요인 발생상황 및 결과
      };

      const result = await putData.mutateAsync(data);
      if (result.status === 200) {
        openDataAlert(message.updateEditSuccess);
      }

      // 수정 모달 초기화
      closeEditModal();
    } catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  };

  // 위험발생상황 및 결과 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const openDeleteModal = () => setDeleteModal({ isOpen: true });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false });
  // DB 업데이트 (삭제 데이터)
  const updateDeleteData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {
      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);
      // 선택된 행(row)의 id 가져오기
      const selectedIds = selectedRows.map((row) => row.danger_cause_id);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteParam: "danger_cause_id",
        param: selectedIds,
      });

      if (result.status === 200) {
        openDataAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearSelection();
    } catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <TableContainer>
        <SituationOverview
          summary={situationSummary}
          sourceFilter={sourceFilter}
          onSourceFilterChange={setSourceFilter}
          searchStatus={searchStatus}
        />
        {/** 위험발생상황 및 결과 테이블 표시 */}
        <NewTableTemplate
          ref={tableRef}
          columns={situationColumns}
          data={filteredSituationData}
          topToolbarContent={customSituationTableTopToolbar}
          renderRowActionContent={isReadOnly ? null : customEditSituationRowAction}
          onSelectedRowsChange={() => {
            const selectedRows = (getSelectedRows() || []).map((row) => row.original);
            setSelectedSituationRow(selectedRows[0] || null);
          }}
          enableCustomGlobalFilter={true}
          enabledSelectionAllRow={!isReadOnly && showAdminMode}
          rowActionsColumnSize={isTabletPortrait ? 160 : 250}
          tableConainerSX={{
            overflowX: "auto",
          }}
          templateSX={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
          }}
        />
        <Alert
          severity="info"
          action={
            !isReadOnly ? (
            <Stack direction="row" spacing={1}>
              {selectedSituationRow ? (
                <Button color="inherit" size="small" onClick={goSelectedRegisterCause}>
                  선택 항목 연결
                </Button>
              ) : null}
              <Button color="inherit" size="small" onClick={goRegisterCause}>
                연결 화면 열기
              </Button>
            </Stack>
            ) : null
          }
          sx={{ mt: 1.5 }}
        >
          {selectedSituationRow
            ? `"${selectedSituationRow.danger_description}" 항목을 설비·공정 평가대상 연결 화면으로 넘길 수 있습니다.`
            : "마스터에 등록된 유해·위험요인을 실제 평가 대상으로 사용하려면 설비·공정별 유해·위험요인 등록 화면에서 연결해주세요."}
        </Alert>
        {/* 행(row) 추가 모달 */}
        <CreateRowModal
          isOpen={showAddModal.isOpen}
          categorys={getFilterListData.data}
          onConfirm={updateAddData}
          onCancel={closeAddModal}
        />
        {/* 행(row) 수정 모달 */}
        <EditRowModal
          isOpen={showEditModal.isOpen}
          originalRowData={showEditModal.rowData}
          categorys={getFilterListData.data}
          onConfirm={updateEditData}
          onCancel={closeEditModal}
        />
        {/* 행(row) 사본 추가 모달 */}
        <CopyRowModal
          isOpen={showCopyModal.isOpen}
          originalRowData={showCopyModal.rowData}
          categorys={getFilterListData.data}
          onConfirm={updateAddData}
          onCancel={closeCopyModal}
        />
        {/* 삭제 메시지 */}
        <ConfirmDialog
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={updateDeleteData}
          onCancel={closeDeleteModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert
          open={dataAlert.isOpen}
          onClose={closeDataAlert}
          message={dataAlert.message}
          severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
        {/* 기타 메시지 박스 알람 */}
        <ConfirmDialogSingle
          open={messageAlarm.isOpen}
          title={messageAlarm.title}
          message={messageAlarm.message}
          onCancel={closeAlarmMessage}
        />
      </TableContainer>
    );
  }

  // StandardSituation 렌더링
  return (
    <Box
      sx={{
        bgcolor: "var(--mui-palette-background-paper)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

// 테이블의 행(row) 생성 모달 컴포넌트
const CreateRowModal = ({ isOpen, categorys, onConfirm, onCancel }) => {
  // const theme = useTheme();
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);

  // 추가 행 데이터 관리
  const [addData, setAddData] = useState([]);
  const clearAddData = () => setAddData([]);

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  const openValidationAlert = (message, severity = "error") => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: "",
      severity: "",
    });
  };

  // 행 추가 완료
  const handleApplyAdd = async () => {
    if (checkEmptyData(addData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    }
    if (await checkValidDBData(addData)) {
      openValidationAlert(message.retryAlreadyNotAdded);
      return;
    }

    onConfirm(addData);
    clearAddData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data) ? [...data] : [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if (tempData.length === 0) {
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      (item) =>
        !item.danger_category || !item.danger_cause || !item.danger_description
    );
    return hasValidData;
  };
  // 데이터 유효성 체크
  const checkValidDBData = async (data) => {
    const params = {
      params: {
        danger_category: data.danger_category,
        danger_cause: data.danger_cause,
        danger_description: data.danger_description,
      },
    };
    const result = await api.get("/getSafeSituationCount", params);
    //console.log(result.data.count);
    return result.data.count > 0;
  };

  // 위험분류 목록
  const dangerCategoryList = Array.from(
    new Set(categorys.map((item) => item.danger_category_filter))
  );
  // 위험원인 목록
  const dangerCauseList = categorys
    .filter((item) => {
      return item.danger_category_filter === addData?.danger_category;
    })
    .flatMap((item) => {
      return item.danger_cause_filters.map((filter) => filter.danger_cause);
    });

  // CreateRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={handleApplyAdd}
      handleCancel={() => {
        clearAddData();
        onCancel();
      }}
      title="추가하기"
      cancelText="취소"
      confirmText="추가"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          gap: 3,
          py: 2,
        }}
      >
        {/* 위험분류 */}
        <FormControl fullWidth>
          <InputLabel>{"위험분류"}</InputLabel>
          <Select
            label={"위험분류"}
            value={addData?.danger_category || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setAddData({
                ...addData,
                danger_category: e.target.value,
                danger_cause: "", // 위험원인 초기화
              });
            }}
          >
            {dangerCategoryList.map((category, index) => (
              <CustomMenuItem key={index} value={category}>
                {category}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>
        {/* 위험원인 */}
        <FormControl fullWidth>
          <InputLabel>{"위험원인"}</InputLabel>
          <Select
            label={"위험원인"}
            value={addData?.danger_cause || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setAddData({
                ...addData,
                danger_cause: e.target.value,
              });
            }}
          >
            {dangerCauseList.map((cause, index) => (
              <CustomMenuItem key={index} value={cause}>
                {cause}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ borderColor: "grey.400", marginX: 2, mt: 2, mb: 2 }} />

        {/* 위험요인 발생상황 및 결과 */}
        <TextField
          label={"유해·위험요인 발생상황 및 결과"}
          value={addData?.danger_description || ""}
          fullWidth
          onChange={(e) => {
            setAddData({
              ...addData,
              danger_description: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{ width: "80%" }}
      />
    </ModalTemplate>
  );
};
// 테이블의 행(row) 수정 모달 컴포넌트
const EditRowModal = ({
  isOpen,
  originalRowData,
  categorys,
  onConfirm,
  onCancel,
}) => {
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);

  // 유효성 검사 알람 상태 관리(빈칸)
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  const openValidationAlert = (message, severity = "error") => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: "",
      severity: "",
    });
  };

  // 수정할 행(row)의 데이터 저장
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(() => {
    setEditData(originalRowData);
  }, [originalRowData]);

  // 행 수정
  const handleEditRowConfirm = async () => {
    if (checkEmptyData(editData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    }
    if (await checkValidDBData(editData)) {
      openValidationAlert(message.retryAlreadyNotChanged);
      return;
    }

    onConfirm(editData);
    clearEditData();
  };

  // 데이터 유효성 체크
  const checkValidDBData = async (data) => {
    const params = {
      params: {
        danger_category: data.danger_category,
        danger_cause: data.danger_cause,
        danger_description: data.danger_description,
      },
    };
    const result = await api.get("/getSafeSituationCount", params);
    //console.log(result.data.count);
    return result.data.count > 0;
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data) ? [...data] : [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if (tempData.length === 0) {
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      (item) =>
        !item.danger_category || !item.danger_cause || !item.danger_description
    );
    return hasValidData;
  };

  // 위험분류 목록
  const dangerCategoryList = Array.from(
    new Set(categorys.map((item) => item.danger_category_filter))
  );
  // 위험원인 목록
  const dangerCauseList = categorys
    .filter((item) => {
      return item.danger_category_filter === editData?.danger_category;
    })
    .flatMap((item) => {
      return item.danger_cause_filters.map((filter) => filter.danger_cause);
    });

  // EditRowModalContent 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={handleEditRowConfirm}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
      contentSX={{ gap: "0px" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          gap: 3,
          py: 2,
        }}
      >
        {/* 위험분류 */}
        <FormControl fullWidth>
          <InputLabel>{"위험분류"}</InputLabel>
          <Select
            label={"위험분류"}
            value={editData?.danger_category || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setEditData({
                ...editData,
                danger_category: e.target.value,
                danger_cause: "", // 위험원인 초기화
              });
            }}
          >
            {dangerCategoryList.map((category, index) => (
              <CustomMenuItem key={index} value={category}>
                {category}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>
        {/* 위험원인 */}
        <FormControl fullWidth>
          <InputLabel>{"위험원인"}</InputLabel>
          <Select
            label={"위험원인"}
            value={editData?.danger_cause || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setEditData({
                ...editData,
                danger_cause: e.target.value,
              });
            }}
          >
            {dangerCauseList.map((cause, index) => (
              <CustomMenuItem key={index} value={cause}>
                {cause}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ borderColor: "grey.400", marginX: 2, mt: 2, mb: 2 }} />

        {/* 위험요인 발생상황 및 결과 */}
        <TextField
          label={"유해·위험요인 발생상황 및 결과"}
          value={editData?.danger_description || ""}
          type="text"
          fullWidth
          //margin="normal"
          onChange={(e) => {
            setEditData({
              ...editData,
              danger_description: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{ width: "80%" }}
      />
    </ModalTemplate>
  );
};
// 테이블의 행(row) 사본 추가 모달 컴포넌트
const CopyRowModal = ({
  isOpen,
  originalRowData,
  categorys,
  onConfirm,
  onCancel,
}) => {
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);

  // 유효성 검사 알람 상태 관리(빈칸)
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  const openValidationAlert = (message, severity = "error") => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: "",
      severity: "",
    });
  };

  // 추가할 행(row)의 데이터 저장
  const [copyData, setCopyData] = useState([]);
  const clearCopyData = () => setCopyData([]);
  useEffect(() => {
    setCopyData(originalRowData);
  }, [originalRowData]);

  // 행 사본 추가
  const handleCopyRowConfirm = async () => {
    if (checkEmptyData(copyData)) {
      openValidationAlert(message.notAllowedEmpty);
      return;
    }
    if (await checkValidDBData(copyData)) {
      openValidationAlert(message.retryAlreadyNotChanged);
      return;
    }

    onConfirm(copyData);
    clearCopyData();
  };

  // 데이터 유효성 체크
  const checkValidDBData = async (data) => {
    const params = {
      params: {
        danger_category: data.danger_category,
        danger_cause: data.danger_cause,
        danger_description: data.danger_description,
      },
    };
    const result = await api.get("/getSafeSituationCount", params);
    //console.log(result.data.count);
    return result.data.count > 0;
  };
  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    const tempData = Array.isArray(data) ? [...data] : [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if (tempData.length === 0) {
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      (item) =>
        !item.danger_category || !item.danger_cause || !item.danger_description
    );
    return hasValidData;
  };

  // 위험분류 목록
  const dangerCategoryList = Array.from(
    new Set(categorys.map((item) => item.danger_category_filter))
  );
  // 위험원인 목록
  const dangerCauseList = categorys
    .filter((item) => {
      return item.danger_category_filter === copyData?.danger_category;
    })
    .flatMap((item) => {
      return item.danger_cause_filters.map((filter) => filter.danger_cause);
    });

  // CopyRowModalContent 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={handleCopyRowConfirm}
      handleCancel={() => {
        clearCopyData();
        onCancel();
      }}
      title="사본 추가"
      cancelText="취소"
      confirmText="추가"
      contentSX={{ gap: "0px" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          gap: 3,
          py: 2,
        }}
      >
        {/* 위험분류 */}
        <FormControl fullWidth>
          <InputLabel>{"위험분류"}</InputLabel>
          <Select
            label={"위험분류"}
            value={copyData?.danger_category || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setCopyData({
                ...copyData,
                danger_category: e.target.value,
                danger_cause: "", // 위험원인 초기화
              });
            }}
          >
            {dangerCategoryList.map((category, index) => (
              <CustomMenuItem key={index} value={category}>
                {category}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>
        {/* 위험원인 */}
        <FormControl fullWidth>
          <InputLabel>{"위험원인"}</InputLabel>
          <Select
            label={"위험원인"}
            value={copyData?.danger_cause || ""}
            onChange={(e) => {
              //console.log(e.target.value);
              setCopyData({
                ...copyData,
                danger_cause: e.target.value,
              });
            }}
          >
            {dangerCauseList.map((cause, index) => (
              <CustomMenuItem key={index} value={cause}>
                {cause}
              </CustomMenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ borderColor: "grey.400", marginX: 2, mt: 2, mb: 2 }} />

        {/* 위험요인 발생상황 및 결과 */}
        <TextField
          label={"유해·위험요인 발생상황 및 결과"}
          value={copyData?.danger_description || ""}
          type="text"
          fullWidth
          //margin="normal"
          onChange={(e) => {
            setCopyData({
              ...copyData,
              danger_description: e.target.value,
            });
          }}
        />
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{ width: "80%" }}
      />
    </ModalTemplate>
  );
};
